import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { Case } from "generated/graphql";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useAppDispatch } from "redux/hooks";
import { updateCaseClientCaseId } from "redux/slices/case";

type CaseIdUpdateProps = {
  open: boolean;
  onClose: () => void;
  mycase: Case;
};

const existsError = "already exists";

export function CaseIdUpdateDialog({ open, onClose, mycase }: CaseIdUpdateProps) {
  const [newClientCaseId, setNewClientCaseId] = useState(mycase.info.clientCaseId);
  const [invalidClientCaseId, setInvalidClientCaseId] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open === true) {
      setInvalidClientCaseId(false);
      setNewClientCaseId(mycase.info.clientCaseId);
    }
  }, [open]);

  const saveClientCaseId = async () => {
    await dispatch(
      updateCaseClientCaseId({
        caseId: mycase.id,
        clientCaseId: newClientCaseId,
      })
    )
      .unwrap()
      .then(() => {
        enqueueSnackbar(`Successfully updated Case ID to ${newClientCaseId}`, {
          variant: "success",
        });
      })
      .then(() => onClose())
      .catch((e) => {
        setInvalidClientCaseId(true);
        const isCaseIdExistsError = e.message.includes(existsError) ?? false;
        const maybeLensId = e.message.split("lens ")[1] ?? undefined;
        const lensIdStr = maybeLensId !== undefined ? ` in Lens ${maybeLensId}` : "";
        const errorMessagePretty: string = isCaseIdExistsError
          ? `Case ID already exists${lensIdStr}`
          : "There was an error updating the case";
        enqueueSnackbar(errorMessagePretty, {
          variant: "error",
        });
      });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Update Case ID</DialogTitle>
      <DialogContent>
        <DialogContentText className="pb-8">
          <b>Ensure new Case ID does not conflict with any existing IDs</b>
        </DialogContentText>
        <TextField
          className="pb-8"
          label="Current Case ID"
          size="small"
          fullWidth
          value={mycase.info.clientCaseId}
          disabled
        />
        <TextField
          autoFocus
          label="New Case ID"
          fullWidth
          value={newClientCaseId}
          onChange={(e) => {
            setInvalidClientCaseId(false);
            setNewClientCaseId(e.target.value);
          }}
          error={invalidClientCaseId}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={saveClientCaseId} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
